import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    useTheme,
    styled,
} from '@mui/material'
import { CheckCircle, Cancel, Close, InfoOutlined } from '@mui/icons-material'
import { Waves, GridOn, ScubaDiving, Assignment } from '@mui/icons-material'
import { z } from 'zod'
import { useZodForm } from 'src/hooks/zod-form'
import { AppForm, FormTextField, FormSubmitButton, FormCheckbox, FormFileUpload } from 'src/components/FormCore'
import { SelectField } from 'src/components/SelectField'
import { FileType } from 'src/enums'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { CancelButton } from 'src/components/CancelButton'
import { AppRoutesNavigation } from 'src/app-routes'
import { useCustomerCtx } from 'src/hooks/context-hooks'
import { toast } from 'react-toastify'
import { getTranslatedText } from 'src/utils'
import {
    useCreateInteractiveSchema,
    useUpdateInteractiveSchema,
    useGetInteractiveSchemaById,
} from 'src/api/useInteractiveSchema'
import { useGetChecklists } from 'src/api/useChecklist'
import { useGetMainComponentTypeList } from 'src/api/main-component-type'

const StyledModuleCard = styled(Card, { shouldForwardProp: prop => prop !== 'checked' })<{ checked: boolean }>(
    ({ theme, checked }) => ({
        cursor: 'pointer',
        height: '160px',
        border: checked ? `2px solid ${theme.palette.success.main}` : `2px solid ${theme.palette.divider}`,
        backgroundColor: checked ? theme.palette.success.light + '20' : theme.palette.background.paper,
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
            boxShadow: theme.shadows[3],
            transform: 'translateY(-2px)',
        },
    }),
)

const StyledModuleCardContent = styled(CardContent)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '24px',
    position: 'relative',
})

const schema = z.object({
    name: z.string().nonempty('Name is required'),
    description: z.string().optional(),
    supportedEquipmentTypeIds: z.array(z.number()).min(0),
    hasWashingSection: z.boolean(),
    hasMeshBreakageSection: z.boolean(),
    hasDivingCertificationSection: z.boolean(),
    hasAttachmentsSection: z.boolean().default(true),
    checklistId: z.number().nullable(),
    procedureFileId: z.number().nullable().optional(),
    guidanceNotes: z.string().optional(),
    isActive: z.boolean().default(true),
})

type FormData = z.infer<typeof schema>

const InteractiveSchemaForm = () => {
    const { id: customerId, schemaId } = useParams<{ id: string; schemaId: string }>()
    const navigate = useNavigate()
    const { t } = useTranslation(['interactive-schema', 'common'])
    const { customer } = useCustomerCtx()
    const theme = useTheme()
    const isEdit = !!schemaId

    const { data: existingSchema } = useGetInteractiveSchemaById(
        customer?.id as number,
        schemaId ? parseInt(schemaId) : 0,
    )
    const { data: checklists } = useGetChecklists(customer?.id as number)
    const { data: equipmentTypesData } = useGetMainComponentTypeList(customer?.id as EntityId)

    const equipmentTypes = equipmentTypesData || []

    const createMutation = useCreateInteractiveSchema()
    const updateMutation = useUpdateInteractiveSchema()

    const [selectedEquipmentTypes, setSelectedEquipmentTypes] = useState<number[]>([])

    const form = useZodForm(schema, {
        defaultValues: {
            name: '',
            description: '',
            supportedEquipmentTypeIds: [],
            hasWashingSection: false,
            hasMeshBreakageSection: false,
            hasDivingCertificationSection: false,
            hasAttachmentsSection: true,
            checklistId: null,
            procedureFileId: null,
            guidanceNotes: '',
            isActive: true,
        },
    })

    const parseConfig = (raw: any) => {
        if (!raw) return {}
        if (typeof raw === 'string') {
            try { return JSON.parse(raw) } catch { return {} }
        }
        return raw
    }

    useEffect(() => {
        if (existingSchema && isEdit) {
            let typeIds = existingSchema.supportedEquipmentTypeIds || []

            if (typeof typeIds === 'string') {
                try {
                    typeIds = JSON.parse(typeIds)
                } catch (e) {
                    typeIds = []
                }
            }

            const config = parseConfig(existingSchema.configuration)

            setSelectedEquipmentTypes(typeIds)
            form.reset({
                name: existingSchema.name,
                description: existingSchema.description || '',
                supportedEquipmentTypeIds: typeIds,
                hasWashingSection: existingSchema.hasWashingSection,
                hasMeshBreakageSection: existingSchema.hasMeshBreakageSection,
                hasDivingCertificationSection: existingSchema.hasDivingCertificationSection,
                hasAttachmentsSection: existingSchema.hasAttachmentsSection,
                checklistId: existingSchema.checklistId ?? null,
                procedureFileId: existingSchema.procedureFileId,
                guidanceNotes: config.notes || '',
                isActive: existingSchema.isActive,
            })
        }
    }, [existingSchema, isEdit, form])

    const handleSubmit = async (data: FormData) => {
        try {
            const { guidanceNotes, ...rest } = data
            const configObject = {
                ...parseConfig(existingSchema?.configuration),
                notes: guidanceNotes || undefined,
            }
            const payload = {
                ...rest,
                configuration: JSON.stringify(configObject),
            }

            if (isEdit && schemaId) {
                await updateMutation.mutateAsync(
                    { customerId: customer?.id as number, id: parseInt(schemaId), data: payload },
                    {
                        onSuccess: () => {
                            toast.success(t('template-updated', { ns: 'interactive-schema' }))
                            navigate(AppRoutesNavigation.Customer.interactiveSchemas(customer?.id as number))
                        },
                    },
                )
            } else {
                await createMutation.mutateAsync({ customerId: customer?.id as number, data: payload }, {
                    onSuccess: () => {
                        toast.success(t('template-created', { ns: 'interactive-schema' }))
                        navigate(AppRoutesNavigation.Customer.interactiveSchemas(customer?.id as number))
                    },
                })
            }
        } catch (error) {
            toast.error(t('error-saving-template', { ns: 'interactive-schema' }))
        }
    }

    const handleEquipmentTypeChange = (event: any, value: any) => {
        const selectedTypes = Array.isArray(value) ? value : []
        const typeIds = selectedTypes.map((type: any) => type.id)
        setSelectedEquipmentTypes(typeIds)
        form.setValue('supportedEquipmentTypeIds', typeIds)
    }

    const ModuleCard = ({
                            name,
                            checked,
                            onChange,
                            icon: IconComponent,
                        }: {
        name: string
        checked: boolean
        onChange: (checked: boolean) => void
        icon?: React.ComponentType<any>
    }) => (
        <StyledModuleCard checked={checked} onClick={() => onChange(!checked)}>
            <StyledModuleCardContent>
                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                    {checked ? (
                        <CheckCircle color="success" sx={{ fontSize: 28 }} />
                    ) : (
                        <Cancel sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                    )}
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                    {IconComponent && (
                        <Box sx={{ color: checked ? theme.palette.success.main : theme.palette.text.secondary }}>
                            <IconComponent sx={{ fontSize: 40 }} />
                        </Box>
                    )}
                    <Typography
                        variant="h5"
                        fontWeight="600"
                        sx={{
                            color: checked ? theme.palette.success.main : theme.palette.text.primary,
                            lineHeight: 1.2,
                        }}
                    >
                        {name}
                    </Typography>
                </Box>
            </StyledModuleCardContent>
        </StyledModuleCard>
    )

    return (
        <CommonPageLayout
            titleSection={{
                title: isEdit
                    ? t('edit-template', { ns: 'interactive-schema' })
                    : t('create-template', { ns: 'interactive-schema' }),
            }}
        >
            <Box p={3} bgcolor={theme.palette.background.paper} borderRadius={1}>
                <AppForm form={form} onSubmit={handleSubmit} hasInitialValues={isEdit}>
                    <Box display="flex" flexDirection="column" gap={3}>
                        <FormTextField
                            name="name"
                            label={t('schema-name', { ns: 'interactive-schema' }) + ' *'}
                            fullWidth
                        />

                        <FormTextField
                            name="description"
                            label={t('description', { ns: 'common' })}
                            fullWidth
                            multiline
                            rows={3}
                        />

                        <Box>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                {t('supported-equipment-types', { ns: 'interactive-schema' })}
                            </Typography>
                            <SelectField
                                multiple
                                data={equipmentTypes || []}
                                getOptionLabel={(option: any) => getTranslatedText(option.name)}
                                value={equipmentTypes?.filter(type => selectedEquipmentTypes.includes(type.id)) || []}
                                onChange={handleEquipmentTypeChange}
                                renderOption={(props: any, option: any) => (
                                    <Box {...props} key={option.id}>
                                        {getTranslatedText(option.name)}
                                    </Box>
                                )}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                label={t('select-equipment-types', { ns: 'interactive-schema' })}
                                filterSelectedOptions
                                placeholder={t('search-equipment-types', { ns: 'interactive-schema' })}
                            />
                            {selectedEquipmentTypes.length === 0 && (
                                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                                    {t('all-equipment-types-default', { ns: 'interactive-schema' })}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Typography variant="h6">
                                    {t('active-modules', { ns: 'interactive-schema' })}
                                </Typography>
                                <Tooltip
                                    title={String(t('active-modules-tooltip', {
                                        ns: 'interactive-schema',
                                    }))}
                                    arrow
                                >
                                    <InfoOutlined sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                {t('click-module-to-activate', { ns: 'interactive-schema' })}
                            </Typography>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                <ModuleCard
                                    name={t('washing', { ns: 'interactive-schema' })}
                                    checked={form.watch('hasWashingSection')}
                                    onChange={checked => form.setValue('hasWashingSection', checked)}
                                    icon={Waves}
                                />
                                <ModuleCard
                                    name={t('mesh-breakage', { ns: 'interactive-schema' })}
                                    checked={form.watch('hasMeshBreakageSection')}
                                    onChange={checked => form.setValue('hasMeshBreakageSection', checked)}
                                    icon={GridOn}
                                />
                                <ModuleCard
                                    name={t('diving-operations', { ns: 'interactive-schema' })}
                                    checked={form.watch('hasDivingCertificationSection')}
                                    onChange={checked => form.setValue('hasDivingCertificationSection', checked)}
                                    icon={ScubaDiving}
                                />
                                <Card sx={{ height: '160px' }}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                            <Assignment sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                            <Typography variant="h6" fontWeight="600">
                                                {t('checklist', { ns: 'interactive-schema' })}
                                            </Typography>
                                        </Box>
                                        <SelectField
                                            data={checklists || []}
                                            getOptionLabel={(option: any) => option.name}
                                            value={checklists?.find(c => c.id === form.watch('checklistId')) ?? null}
                                            onChange={(_, value) => {
                                                const selected = value as any
                                                form.setValue('checklistId', selected?.id ?? null)
                                            }}
                                            renderOption={(props: any, option: any) => (
                                                <Box {...props} key={option.id}>
                                                    {option.name}
                                                </Box>
                                            )}
                                            isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                                            label={t('select-checklist', { ns: 'interactive-schema' })}
                                            size="small"
                                        />
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="h6" mb={1}>
                                {t('documents-notes-procedures', { ns: 'interactive-schema' })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                {t('attach-supporting-materials', { ns: 'interactive-schema' })}
                            </Typography>

                            <Box mb={3}>
                                {form.watch('procedureFileId') ? (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        p={2}
                                        border="1px solid"
                                        borderColor="divider"
                                        borderRadius={1}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('procedure-file-uploaded', { ns: 'interactive-schema' })}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => form.setValue('procedureFileId', null)}
                                            color="error"
                                        >
                                            <Close />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <FormFileUpload
                                        name="procedureFileId"
                                        label={t('upload-procedure-file', { ns: 'interactive-schema' })}
                                        mapFileKey="id"
                                        type={FileType.File}
                                    />
                                )}
                            </Box>

                            <FormTextField
                                name="guidanceNotes"
                                label={t('guidance-notes', { ns: 'interactive-schema' })}
                                fullWidth
                                multiline
                                rows={4}
                                helperText={t('guidance-notes-help', { ns: 'interactive-schema' })}
                            />
                        </Box>

                        <FormCheckbox
                            name="isActive"
                            label={t('template-active', { ns: 'interactive-schema' })}
                        />

                        <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                            <CancelButton
                                onClick={() => navigate(AppRoutesNavigation.Customer.interactiveSchemas(customerId!))}
                            />
                            <FormSubmitButton
                                createText={t('create', { ns: 'common' })}
                                updateText={t('save', { ns: 'common' })}
                            />
                        </Box>
                    </Box>
                </AppForm>
            </Box>
        </CommonPageLayout>
    )
}

export default InteractiveSchemaForm